import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileText, Clock, CheckCircle2, XCircle, AlertTriangle, 
  Building2, Calendar, RefreshCw, Search,
  ChevronRight, Filter, Home, ArrowLeft, Timer, Ban,
  Scale, Handshake, FileSignature, MessageSquare, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import AEDIcon from '@/components/icons/AEDIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import api from '@/services/api';

// ============ CONSTANTS ============

const DEAL_STATE_CONFIG = {
  deal_none: { label: 'No Deal', color: 'bg-gray-600', textColor: 'text-gray-400', icon: Clock },
  deal_negotiating: { label: 'Negotiating', color: 'bg-blue-600', textColor: 'text-blue-400', icon: MessageSquare },
  deal_offer_accepted: { label: 'Offer Accepted', color: 'bg-green-600', textColor: 'text-green-400', icon: CheckCircle2 },
  deal_form_f_pending: { label: 'Form F Pending', color: 'bg-gold-600', textColor: 'text-gold-400', icon: FileText },
  deal_form_f_signed: { label: 'Form F Signed', color: 'bg-emerald-600', textColor: 'text-emerald-400', icon: FileSignature },
  deal_extension_requested: { label: 'Extension Requested', color: 'bg-orange-600', textColor: 'text-orange-400', icon: Timer },
  deal_extension_accepted: { label: 'Extension Accepted', color: 'bg-teal-600', textColor: 'text-teal-400', icon: Calendar },
  deal_termination_requested: { label: 'Termination Requested', color: 'bg-red-600', textColor: 'text-red-400', icon: Ban },
  deal_terminated_mutual: { label: 'Terminated', color: 'bg-gray-600', textColor: 'text-gray-400', icon: Handshake },
  deal_dispute_mode: { label: 'Dispute', color: 'bg-red-700', textColor: 'text-red-400', icon: Scale },
  deal_closed: { label: 'Closed', color: 'bg-green-700', textColor: 'text-green-400', icon: CheckCircle2 },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All Deals' },
  { value: 'deal_negotiating', label: 'Negotiating' },
  { value: 'deal_form_f_signed', label: 'Form F Signed' },
  { value: 'deal_extension_requested', label: 'Extension Requested' },
  { value: 'deal_termination_requested', label: 'Termination Requested' },
  { value: 'deal_dispute_mode', label: 'In Dispute' },
  { value: 'deal_closed', label: 'Closed' },
  { value: 'deal_terminated_mutual', label: 'Terminated' },
];

// ============ HELPER FUNCTIONS ============

const formatCurrency = (amount) => {
  if (!amount) return 'AED 0';
  return `AED ${Number(amount).toLocaleString()}`;
};

const formatDate = (ts) => {
  if (!ts) return 'N/A';
  const date = new Date(ts);
  return date.toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatRelativeTime = (ts) => {
  if (!ts) return 'N/A';
  const date = new Date(ts);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  
  return formatDate(ts);
};

// ============ COMPONENTS ============

const DealStateBadge = ({ state }) => {
  const config = DEAL_STATE_CONFIG[state] || DEAL_STATE_CONFIG.deal_none;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} text-white px-2 py-1 text-xs`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

const DealCard = ({ deal, onClick }) => {
  const stateConfig = DEAL_STATE_CONFIG[deal.deal_state] || DEAL_STATE_CONFIG.deal_none;
  const StateIcon = stateConfig.icon;
  
  const isActive = !['deal_closed', 'deal_terminated_mutual'].includes(deal.deal_state);
  const needsAction = ['deal_extension_requested', 'deal_termination_requested', 'deal_dispute_mode'].includes(deal.deal_state);
  
  return (
    <Card 
      className={`border-white/10 hover:border-white/30 transition-all cursor-pointer group ${needsAction ? 'border-gold-600/50' : ''}`}
      onClick={onClick}
      data-testid={`deal-card-${deal.id}`}
    >
      <CardContent className="p-0">
        <div className="flex">
          {/* Property Image */}
          <div className="w-32 h-32 flex-shrink-0 relative overflow-hidden rounded-l-lg">
            {deal.property?.images?.[0] ? (
              <img 
                src={deal.property.images[0]} 
                alt={deal.property?.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            ) : (
              <div className="w-full h-full bg-white/5 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white/20" />
              </div>
            )}
            {needsAction && (
              <div className="absolute top-2 left-2">
                <Badge className="bg-gold-600 text-white text-[10px] px-1.5 py-0.5">
                  Action Needed
                </Badge>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 p-4 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-muted-foreground font-mono">
                    {deal.id?.slice(0, 8).toUpperCase()}
                  </span>
                  <DealStateBadge state={deal.deal_state} />
                </div>
                <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                  {deal.property?.title || 'Property'}
                </h3>
                <p className="text-sm text-muted-foreground truncate">
                  {deal.property?.location?.community || deal.property?.location?.area || 'Dubai'}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            </div>
            
            <Separator className="my-3" />
            
            <div className="flex items-center justify-between text-sm">
              <div>
                <p className="text-xs text-muted-foreground">
                  {deal.user_role === 'seller' ? 'Selling to' : deal.user_role === 'buyer' ? 'Buying from' : 'Between'}
                </p>
                <p className="font-medium">
                  {deal.user_role === 'seller' ? deal.buyer?.full_name : 
                   deal.user_role === 'buyer' ? deal.seller?.full_name :
                   `${deal.seller?.full_name?.slice(0, 8)} / ${deal.buyer?.full_name?.slice(0, 8)}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Deal Value</p>
                <p className="font-bold text-primary">
                  {formatCurrency(deal.accepted_offer_amount)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>Deposit: {formatCurrency(deal.deposit_amount)}</span>
              <span>Updated {formatRelativeTime(deal.updated_at)}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EmptyState = ({ hasFilter }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
      <FileText className="w-8 h-8 text-muted-foreground/50" />
    </div>
    <h3 className="text-lg font-medium mb-2">No Deals Found</h3>
    <p className="text-sm text-muted-foreground max-w-sm">
      {hasFilter 
        ? "No deals match your current filter. Try adjusting your search criteria."
        : "You don't have any deals yet. Deals are created when an offer is accepted on a property."}
    </p>
  </div>
);

const StatsCard = ({ icon: Icon, label, value, subtext, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary/10 border-primary/30 text-primary',
    green: 'bg-green-950/20 border-green-600/30 text-green-400',
    yellow: 'bg-gold-950/20 border-gold-600/30 text-gold-400',
    red: 'bg-red-950/20 border-red-600/30 text-red-400',
    blue: 'bg-blue-950/20 border-blue-600/30 text-blue-400',
  };
  
  return (
    <Card className={`${colorClasses[color]} border`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 opacity-70" />
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs opacity-70">{label}</p>
            {subtext && <p className="text-xs opacity-50 mt-0.5">{subtext}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ MAIN COMPONENT ============

const MyDealsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Fetch deals
  const fetchDeals = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await api.get(`/deals?${params.toString()}`);
      setDeals(response.data.deals || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load deals');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);
  
  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);
  
  // Filter deals by search term
  const filteredDeals = deals.filter(deal => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      deal.id?.toLowerCase().includes(search) ||
      deal.property?.title?.toLowerCase().includes(search) ||
      deal.property?.location?.community?.toLowerCase().includes(search) ||
      deal.seller?.full_name?.toLowerCase().includes(search) ||
      deal.buyer?.full_name?.toLowerCase().includes(search)
    );
  });
  
  // Calculate stats
  const stats = {
    total: deals.length,
    active: deals.filter(d => !['deal_closed', 'deal_terminated_mutual'].includes(d.deal_state)).length,
    needsAction: deals.filter(d => ['deal_extension_requested', 'deal_termination_requested', 'deal_dispute_mode'].includes(d.deal_state)).length,
    totalValue: deals.reduce((sum, d) => sum + (d.accepted_offer_amount || 0), 0)
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your deals...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="my-deals-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">My Deals</h1>
                <p className="text-sm text-muted-foreground">
                  {user?.role === 'admin' ? 'All platform deals' : 'Your active and completed transactions'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchDeals}
                disabled={refreshing}
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatsCard 
            icon={FileText}
            label="Total Deals"
            value={stats.total}
            color="blue"
          />
          <StatsCard 
            icon={Clock}
            label="Active"
            value={stats.active}
            color="green"
          />
          <StatsCard 
            icon={AlertTriangle}
            label="Needs Action"
            value={stats.needsAction}
            color={stats.needsAction > 0 ? 'yellow' : 'primary'}
          />
          <StatsCard 
            icon={AEDIcon}
            label="Total Value"
            value={formatCurrency(stats.totalValue).replace('AED ', '')}
            subtext="AED"
            color="primary"
          />
        </div>
        
        {/* Filters */}
        <Card className="border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search deals by property, ID, or participant..."
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((filter) => (
                    <SelectItem key={filter.value} value={filter.value}>
                      {filter.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
        {/* Deals List */}
        {filteredDeals.length > 0 ? (
          <div className="space-y-4">
            {filteredDeals.map((deal) => (
              <DealCard 
                key={deal.id}
                deal={deal}
                onClick={() => navigate(`/deals/${deal.id}`)}
              />
            ))}
          </div>
        ) : (
          <Card className="border-white/10">
            <CardContent>
              <EmptyState hasFilter={searchTerm || statusFilter !== 'all'} />
            </CardContent>
          </Card>
        )}
        
        {/* Results info */}
        {filteredDeals.length > 0 && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            Showing {filteredDeals.length} of {deals.length} deals
          </p>
        )}
      </main>
    </div>
  );
};

export default MyDealsPage;
